"use client"

import type React from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import Link from "next/link"
import { tools, categories, getToolsByCategory, getNewTools, getPopularTools, type Tool } from "@/lib/tools-registry"
import { useTools } from "@/components/tools-context"
import { openCommandPalette } from "@/lib/command-palette-events"
import { Star, Sparkles, ArrowRight, Search } from "lucide-react"

function ToolCard({ tool }: { tool: Tool }) {
  const { isFavorite, toggleFavorite } = useTools()
  const favorite = isFavorite(tool.id)

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    toggleFavorite(tool.id)
  }

  return (
    <div className="group relative h-full">
      <Link href={tool.href} className="block h-full rounded-xl">
        <Card className="h-full transition-all hover:shadow-lg hover:border-primary/50 hover:-translate-y-0.5">
          <CardHeader className="flex flex-row items-start justify-between pb-2 pr-12 space-y-0">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <tool.icon className="h-4 w-4" />
              </div>
              <div>
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  {tool.title}
                  {tool.isNew && (
                    <Badge variant="secondary" className="text-xs px-1.5 py-0 bg-primary/10 text-primary">
                      New
                    </Badge>
                  )}
                </CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <CardDescription className="line-clamp-2">{tool.description}</CardDescription>
          </CardContent>
        </Card>
      </Link>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={favorite ? `Remove ${tool.title} from favorites` : `Add ${tool.title} to favorites`}
        aria-pressed={favorite}
        title={favorite ? "Remove from favorites" : "Add to favorites"}
        className={`absolute right-3 top-3 z-10 h-8 w-8 transition-opacity ${favorite ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100"}`}
        onClick={handleFavoriteClick}
      >
        <Star
          aria-hidden="true"
          className={`h-4 w-4 ${favorite ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground"}`}
        />
      </Button>
    </div>
  )
}

function CategorySection({ categoryId }: { categoryId: string }) {
  const category = categories.find((c) => c.id === categoryId)
  if (!category) return null

  const categoryTools = getToolsByCategory(category.id)
  const CategoryIcon = category.icon

  return (
    <section className="mb-10">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-primary/10">
          <CategoryIcon className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold">{category.title}</h2>
          <p className="text-sm text-muted-foreground">{category.description}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {categoryTools.map((tool) => (
          <ToolCard key={tool.id} tool={tool} />
        ))}
      </div>
    </section>
  )
}

function FeaturedSection() {
  const newTools = getNewTools().slice(0, 6)
  const popularTools = getPopularTools().slice(0, 6)

  return (
    <div className="grid md:grid-cols-2 gap-6 mb-10">
      {/* New Tools */}
      <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-primary" />
            New Tools
          </CardTitle>
          <CardDescription>Recently added to the toolkit</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {newTools.map((tool) => (
            <Link
              key={tool.id}
              href={tool.href}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-background/50 transition-colors group"
            >
              <tool.icon className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
              <span className="flex-1 text-sm">{tool.title}</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          ))}
        </CardContent>
      </Card>

      {/* Popular Tools */}
      <Card className="bg-gradient-to-br from-orange-500/5 to-orange-500/10 border-orange-500/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Star className="h-5 w-5 text-orange-500" />
            Popular Tools
          </CardTitle>
          <CardDescription>Most used by developers</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {popularTools.map((tool) => (
            <Link
              key={tool.id}
              href={tool.href}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-background/50 transition-colors group"
            >
              <tool.icon className="h-4 w-4 text-muted-foreground group-hover:text-orange-500" />
              <span className="flex-1 text-sm">{tool.title}</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}

function HeroSection() {
  return (
    <div className="text-center mb-12 py-8">
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
        Free Online{" "}
        <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
          Developer Tools
        </span>
      </h1>
      <p className="text-muted-foreground max-w-2xl mx-auto mb-6 text-lg">
        {tools.length} tools for developers, designers, and teams.
        PDF tools, JSON formatters, encoders, and more. No signup required.
      </p>
      <Button
        variant="outline"
        size="lg"
        onClick={openCommandPalette}
        className="gap-2"
      >
        <Search className="h-4 w-4" />
        Search tools...
        <kbd className="ml-2 hidden sm:inline-flex h-5 items-center gap-0.5 rounded border bg-muted px-1.5 font-mono text-[10px] text-muted-foreground">
          ⌘/Ctrl K
        </kbd>
      </Button>
    </div>
  )
}

function StatsBar() {
  const newCount = getNewTools().length
  const categoryCount = categories.length

  return (
    <div className="flex flex-wrap justify-center gap-6 mb-10 text-sm text-muted-foreground">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-foreground">{tools.length}</span>
        <span>Total Tools</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="font-semibold text-foreground">{categoryCount}</span>
        <span>Categories</span>
      </div>
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <span className="font-semibold text-foreground">{newCount}</span>
        <span>New Tools</span>
      </div>
    </div>
  )
}

export default function Home() {
  return (
    <main className="container py-6 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
      <HeroSection />
      <StatsBar />
      <FeaturedSection />

      {categories.map((category) => (
        <CategorySection key={category.id} categoryId={category.id} />
      ))}

      <div className="text-center py-12 border-t mt-12">
        <h2 className="text-xl font-semibold mb-2">More tools coming soon</h2>
        <p className="text-muted-foreground">
          We&apos;re constantly adding new tools. Stay tuned!
        </p>
      </div>
    </main>
  )
}
